/**
 * «ЗП» → «Индексация» (`?view=index`, право `salary_index`): часовая ставка по стажу — разделы «Стилисты»,
 * «Комплектовщики», «Руководители». Правило HR: год — с первого рабочего дня (первая смена с акцептом, HR может
 * поправить дату); стилист и руководитель 150 → 160 → 190 → +20 ₽, комплектовщик 100 → +20 ₽; увольнение (учётка
 * выключена от 1 дня) обнуляет стаж; список HR «без индексации» — только годовщина; подпись «гарант» — ставка
 * гарантии из карточки CRM.
 * Числа — те же, что у `prokat-runner salary-index`, из `billing/salary_index.py`; кнопка «Индексировать» —
 * `SalaryRaiseDialog`; спека `docs/specs/2026-11-04-salary-index-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Combo } from "@/components/Combo";
import { SalaryRaiseDialog } from "@/components/SalaryRaiseDialog";
import { money } from "@/lib/bonus";
import { type IndexRow, type IndexStatus, loadSalaryIndex, type SalaryIndex, setSalaryIndexException, setSalaryIndexStart } from "@/lib/salary";

type Filter = "all" | "due" | "soon" | "ok" | "above" | "fixed" | "returned" | "idle";

/** Разделы по роли `sUser.userRoleID`. */
const SECTIONS = [
  { role: 2, title: "Стилисты" },
  { role: 4, title: "Комплектовщики" },
  { role: 6, title: "Руководители" },
];

const date = (d: string | null) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : "—");
const cell = "px-3 py-2 text-left align-top";
const first = "sticky left-0 z-10 bg-bg-subtle px-4 py-2 text-left align-top";
const years = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? "год" : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? "года" : "лет");

const STATUS: Record<IndexStatus, { label: string; tone: string }> = {
  due: { label: "Пора индексировать", tone: "bg-err-bg text-err" },
  ok: { label: "По стажу", tone: "bg-ok-bg text-ok" },
  above: { label: "Выше стажа", tone: "bg-warn-bg text-warn" },
  fixed: { label: "Без индексации", tone: "bg-accent-dim text-accent-text" },
  no_shifts: { label: "Смен ещё нет", tone: "bg-neutral-bg text-fg-muted" },
};

function pass(r: IndexRow, f: Filter): boolean {
  if (f === "all") return true;
  if (f === "idle") return r.idle;
  if (r.idle) return false;
  if (f === "soon") return r.soon;
  if (f === "returned") return r.returned !== null;
  return r.status === f;
}

/** Список HR «без индексации»: внести с заметкой («старший СПб») или вернуть индексацию. */
function FixedToggle({ r }: { r: IndexRow }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: (on: boolean) => setSalaryIndexException(r.user.id, on, note),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["salary-index"] });
    },
  });
  const link = "text-[11.5px] text-fg-muted underline decoration-dotted underline-offset-2 hover:text-fg disabled:opacity-40";
  if (r.status === "fixed")
    return (
      <button type="button" className={`${link} mt-1 block`} disabled={save.isPending} onClick={() => save.mutate(false)}>
        вернуть индексацию
      </button>
    );
  if (!open)
    return (
      <button type="button" className={`${link} mt-1 block`} onClick={() => setOpen(true)} title="старший, руководитель, зам — ставка вручную">
        без индексации
      </button>
    );
  return (
    <span className="mt-1 flex items-center gap-1">
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="кто: старший СПб, зам МСК…"
        aria-label="Кто он"
        className="h-7 w-44 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px] text-fg outline-none focus:border-accent"
      />
      <button
        type="button"
        className="h-7 rounded-[var(--radius-ui)] border border-accent px-2 text-[12px] text-fg hover:bg-bg-hover disabled:opacity-40"
        disabled={save.isPending}
        onClick={() => save.mutate(true)}
      >
        Сохранить
      </button>
      <button type="button" className={link} onClick={() => setOpen(false)}>
        отмена
      </button>
      {save.isError && <span className="text-[11.5px] text-err">{String(save.error.message)}</span>}
    </span>
  );
}

function Status({ r, onRaise }: { r: IndexRow; onRaise: (id: number) => void }) {
  const s = STATUS[r.status];
  return (
    <>
      <span className={`inline-block rounded-[var(--radius-ui)] px-2 py-0.5 text-[12px] font-medium ${s.tone}`}>{s.label}</span>
      {r.status === "due" && (
        <span className="mt-1 block text-[12px]">
          <b className="font-semibold tabular-nums">
            {money(r.rate)} → {money(r.expected ?? 0)} ₽
          </b>
          <span className="block text-fg-muted">с {date(r.overdue_since)}</span>
          <button
            type="button"
            onClick={() => onRaise(r.user.id)}
            className="mt-1 h-7 rounded-[var(--radius-ui)] border border-accent px-2.5 text-[12px] text-fg hover:bg-bg-hover"
          >
            Индексировать
          </button>
        </span>
      )}
      {r.early && <span className="mt-1 block text-[11.5px] text-fg-muted">поднята заранее, к годовщине</span>}
      {r.status === "above" && (
        <span className="mt-1 block text-[11.5px] text-fg-muted">
          {r.leave_manual
            ? `ставка при уходе ${money(r.base)} ₽ была выше стажа`
            : r.legacy
              ? "ставка не из лестницы"
              : `по стажу было бы ${money(r.expected ?? 0)} ₽`}
          <span className="block">не в списке HR — индексацию не предлагаем</span>
        </span>
      )}
      {r.status === "fixed" && (
        <span className="mt-1 block text-[11.5px] text-fg-muted">{r.fixed_note || "список HR"}: ставка вручную</span>
      )}
      {r.status !== "no_shifts" && <FixedToggle r={r} />}
      {r.idle && <span className="mt-1 block text-[11.5px] text-warn">не работает: последняя смена {date(r.last_day)}</span>}
    </>
  );
}

/** Дата старта вручную: HR знает первый рабочий день лучше CRM («AI УЦ» #48) — или вернуть расчёт по CRM. */
function StartEdit({ r }: { r: IndexRow }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(r.start ?? "");
  const [note, setNote] = useState(r.start_note ?? "");
  const save = useMutation({
    mutationFn: (on: boolean) => setSalaryIndexStart(r.user.id, on ? day : null, on ? note : ""),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["salary-index"] });
    },
  });
  const link = "text-[11.5px] text-fg-muted underline decoration-dotted underline-offset-2 hover:text-fg disabled:opacity-40";
  const field = "h-7 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px] text-fg outline-none focus:border-accent";
  if (!open)
    return (
      <span className="mt-1 flex gap-2">
        <button type="button" className={link} onClick={() => setOpen(true)} title="первый рабочий день по HR — от него годовщины">
          исправить дату
        </button>
        {r.start_reason === "manual" && (
          <button type="button" className={link} disabled={save.isPending} onClick={() => save.mutate(false)}>
            вернуть по CRM
          </button>
        )}
      </span>
    );
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1">
      <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Первый рабочий день" className={field} />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="почему: по HR, 5 лет 11.11" aria-label="Почему" className={`${field} w-44`} />
      <button
        type="button"
        className="h-7 rounded-[var(--radius-ui)] border border-accent px-2 text-[12px] text-fg hover:bg-bg-hover disabled:opacity-40"
        disabled={save.isPending || !day}
        onClick={() => save.mutate(true)}
      >
        Сохранить
      </button>
      <button type="button" className={link} onClick={() => setOpen(false)}>
        отмена
      </button>
      {save.isError && <span className="text-[11.5px] text-err">{String(save.error.message)}</span>}
    </span>
  );
}

function Start({ r }: { r: IndexRow }) {
  if (!r.start) return <span className="text-fg-faint">смен с акцептом ещё нет, учётка {date(r.created)}</span>;
  const dismissals = r.gaps.filter((g) => g.dismissal);
  const short = r.gaps.filter((g) => !g.dismissal);
  return (
    <>
      <span className="tabular-nums">{date(r.start)}</span>
      <span className="block text-[11.5px] text-fg-muted">
        {r.start_reason === "manual"
          ? `вручную HR${r.start_note ? `: ${r.start_note}` : ""}; по CRM ${date(r.start_auto)}`
          : r.start_reason === "return"
            ? `после возврата, ставка при уходе ${money(r.base)} ₽`
            : "первая смена с акцептом"}
      </span>
      {r.attestation && (
        <span className="block text-[11.5px] text-fg-faint" title="статус «Ат-1/Ат-2» ставит Робот по онлайн-тесту — на старт не влияет">
          тест «Ат» {date(r.attestation)}
        </span>
      )}
      {r.legacy && (
        <span className="block text-[11.5px] text-fg-muted" title="пришедшим до 20.11.2023 ставку не пересчитываем">
          до 20.11.2023: ставка на 04.11.2026 как есть
        </span>
      )}
      {dismissals.map((g) => (
        <span key={g.from} className="block text-[11.5px] text-fg-faint" title="в стаж не входит: стаж с первого дня после возврата">
          увольнение {date(g.from)} – {date(g.to)}
        </span>
      ))}
      {short.map((g) => (
        <span key={g.from} className="block text-[11.5px] text-fg-faint" title="выключили и включили в те же сутки — не увольнение">
          выключение {date(g.from)}, в тот же день
        </span>
      ))}
      <StartEdit r={r} />
    </>
  );
}

function Rows({ title, rows, onRaise }: { title: string; rows: IndexRow[]; onRaise: (id: number) => void }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`индексация: ${title.toLowerCase()}`}>
      <h2 className="px-4 pt-3 text-[13px] font-semibold text-fg">
        {title} <span className="font-normal tabular-nums text-fg-muted">{rows.length}</span>
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-muted">
            <tr>
              <th className={`${first} font-medium`}>Сотрудник</th>
              <th className={`${cell} text-right font-medium`}>Ставка</th>
              <th className={`${cell} font-medium`}>Индексация</th>
              <th className={`${cell} font-medium`}>Стаж с</th>
              <th className={`${cell} font-medium`}>Следующая</th>
              <th className={`${cell} font-medium`}>Как менялась ставка</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user.id} className={`border-t border-border ${r.idle ? "opacity-60" : ""}`} data-annot={`индексация ${r.user.name}`}>
                <th scope="row" className={`${first} font-normal`}>
                  <span className="font-medium">{r.user.name}</span>
                  <span className="block text-[11.5px] text-fg-muted">{r.location.name ?? "—"}</span>
                </th>
                <td className={`${cell} whitespace-nowrap text-right`}>
                  <span className="text-[15px] font-semibold tabular-nums">{money(r.rate)} ₽</span>
                  {r.guarantee > 0 && (
                    <span className="mt-0.5 block" title="гарантированная ставка часа из карточки CRM (zpGarantPriceHour)">
                      <span className="inline-block rounded-[var(--radius-ui)] bg-accent-dim px-1.5 py-0.5 text-[11px] font-medium text-accent-text tabular-nums">
                        гарант {money(r.guarantee)} ₽
                      </span>
                    </span>
                  )}
                  {r.years !== null && (
                    <span className="block text-[11.5px] text-fg-muted">
                      {r.years === 0 ? "первый год" : `стаж ${r.years} ${years(r.years)}`}
                    </span>
                  )}
                </td>
                <td className={cell}>
                  <Status r={r} onRaise={onRaise} />
                </td>
                <td className={`${cell} whitespace-nowrap`}>
                  <Start r={r} />
                </td>
                <td className={`${cell} whitespace-nowrap`}>
                  {r.next_date ? (
                    <>
                      <span className="tabular-nums">{date(r.next_date)}</span>
                      {r.status === "fixed" ? (
                        <span className="block text-[12px]">годовщина — поздравить</span>
                      ) : (
                        <span className="block text-[12px] tabular-nums">→ {money(r.next_rate ?? 0)} ₽</span>
                      )}
                      {r.soon && <span className="block text-[11.5px] font-medium text-warn">скоро</span>}
                    </>
                  ) : (
                    <span className="text-fg-faint">·</span>
                  )}
                </td>
                <td className={cell}>
                  {r.history.length ? (
                    r.history.slice(-4).map((h) => (
                      <span key={h.date} className="block whitespace-nowrap text-[11.5px] tabular-nums text-fg-muted">
                        {date(h.date)}: {h.was ?? "—"} → {h.now ?? "—"}
                      </span>
                    ))
                  ) : (
                    <span className="text-[11.5px] text-fg-faint">не менялась</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function How({ d }: { d: SalaryIndex }) {
  const b = "font-medium text-fg";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted" data-annot="как считается индексация">
      <h2 className="text-[13px] font-semibold text-fg">Как считается индексация</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {d.rule.ladders.map((l) => (
          <li key={l.role}>
            <b className={b}>{l.name}</b>: первый год {money(l.start)} ₽/ч, каждую годовщину{" "}
            {[l.start, ...l.steps].map((x) => money(x)).join(" → ")}
            {l.steps.length ? "" : ` → ${money(l.start + l.step)}`} ₽, дальше +{l.step} ₽ в год.
          </li>
        ))}
        <li>
          <b className={b}>Стаж</b> — с первого рабочего дня после аттестации. В CRM это первая смена с акцептом: учебные дни не акцептуют, а статус «Ат-1/Ат-2»
          ставит Робот по онлайн-тесту, иногда уже после первых смен. Если HR знает дату точнее — «исправить дату» в строке, рядом останется дата по CRM.
        </li>
        <li>
          <b className={b}>Увольнение</b> — учётка выключена от {d.rule.gap_days} дня. Стаж с нуля: вернувшийся остаётся на ставке, на которой ушёл, следующая
          индексация — через год после первого дня после возврата.
        </li>
        <li>
          <b className={b}>Пришедшие до {date(d.rule.legacy_before)}</b>: ставку на {date(d.rule.baseline)} не пересчитываем, дальше — их годовщины.
        </li>
        <li>
          <b className={b}>Пора индексировать</b> — ставка ниже положенной по стажу: с какой годовщины и до какой ставки. Кнопка «Индексировать» записывает
          ставку в карточку CRM и отправляет сотруднику сообщение.
        </li>
        <li>
          <b className={b}>Без индексации</b> — список HR: старшие стилисты, руководители и замы СПб и МСК. Ставка вручную, отчёт напоминает только о
          годовщине работы — поздравить. Внести и убрать — «без индексации» / «вернуть индексацию» в строке.{" "}
          <b className={b}>Выше стажа</b> — ставка выше положенной, а в списке HR сотрудника нет: индексацию не предлагаем, проверьте.
        </li>
        <li>
          <b className={b}>Гарант</b> — гарантированная ставка часа из карточки CRM: если за неделю начислено меньше, CRM доплачивает до неё. На индексацию
          сама не влияет — кого не индексировать, решает список HR. <b className={b}>После возврата</b> — кто увольнялся и вернулся: стаж с первого дня
          после возврата.
        </li>
      </ul>
    </section>
  );
}

export function IndexReport() {
  const q = useQuery({ queryKey: ["salary-index"], queryFn: loadSalaryIndex });
  const [filter, setFilter] = useState<Filter>("all");
  const [salon, setSalon] = useState("");
  const [raise, setRaise] = useState<number | null>(null);
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Читаю ставки и стаж из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить индексацию: {String(q.error.message)}</div>;
  const d = q.data;
  const salons = [...new Set(d.rows.map((r) => r.location.name ?? "—"))].sort();
  const inSalon = d.rows.filter((r) => !salon || (r.location.name ?? "—") === salon);
  const count = (f: Filter) => inSalon.filter((r) => pass(r, f)).length;
  const chips: { id: Filter; label: string; tone: string }[] = [
    { id: "all", label: "Все", tone: "" },
    { id: "due", label: "Пора индексировать", tone: "text-err" },
    { id: "soon", label: "Годовщина за 30 дней", tone: "text-warn" },
    { id: "ok", label: "По стажу", tone: "text-ok" },
    { id: "above", label: "Выше стажа", tone: "text-warn" },
    { id: "fixed", label: "Без индексации", tone: "text-accent-text" },
    { id: "returned", label: "После возврата", tone: "text-fg" },
    { id: "idle", label: "Не работают", tone: "text-fg-muted" },
  ];
  const rows = inSalon.filter((r) => pass(r, filter));
  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-annot="отбор индексации">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={filter === c.id}
            onClick={() => setFilter(c.id)}
            className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-3 py-1.5 ${
              filter === c.id ? "border-accent bg-accent-dim" : "border-border hover:bg-bg-hover"
            }`}
          >
            <span className={`text-[16px] font-semibold tabular-nums ${c.tone}`}>{count(c.id)}</span>
            <span className="text-[12.5px] text-fg-muted">{c.label}</span>
          </button>
        ))}
        <div className="ml-auto">
          <Combo
            label="Салон"
            value={salon}
            options={[{ value: "", label: "Все салоны" }, ...salons.map((s) => ({ value: s, label: s }))]}
            onChange={setSalon}
            placeholder="салон"
            width={220}
          />
        </div>
      </div>
      {rows.length ? (
        SECTIONS.map((sec) => {
          const part = rows.filter((r) => r.role.id === sec.role);
          return part.length ? <Rows key={sec.role} title={sec.title} rows={part} onRaise={setRaise} /> : null;
        })
      ) : (
        <div className="text-[12.5px] text-fg-muted">Здесь никого нет.</div>
      )}
      <How d={d} />
      {raise !== null && <SalaryRaiseDialog user={raise} onClose={() => setRaise(null)} />}
    </>
  );
}
